import { useTranslation } from "react-i18next";
import _ from "lodash";
import moment from "moment";
import { ApolloError, useQuery, useSubscription } from "@apollo/react-hooks";
import { DocumentNode } from "graphql";
import {
  MY_CAFETERIA_GET_ARCHIVED_STATEMENT_ITEMS,
  MY_CAFETERIA_SUBSCRIPTION,
} from "my-cafeteria/graphql";
import { useOcPolling } from "oc/hooks";
import parseJdDate from "oc/utils/parse-jd-date";

export interface ArchivedItem {
  plan?: boolean;
  periodStartDate: string;
  startDate: string;
  endDate: string;
  cost: number;
  amount: number;
  menuItem?: { name: string };
  origin: string;
}

export interface ParsedArchivedItem {
  index: number;
  periodStartDate: string;
  originalStartDate: string;
  startDate: string;
  endDate: string;
  originalEndDate: string;
  months: string;
  numberOfMonths: number;
  valueOfBenefit: number;
  cost: number;
  benefit?: string;
  origin: string;
  amount: number;
  menuItem?: {
    code: string;
    isReusable?: boolean;
  };
}

interface UseArchivedStatementItemsOptions {
  contractCode?: string;
}

interface UseArchivedStatementItemsResult {
  data: ParsedArchivedItem[];
  loading: boolean;
  error?: ApolloError;
}

export const useArchivedStatementItems = ({
  contractCode,
}: UseArchivedStatementItemsOptions): UseArchivedStatementItemsResult => {
  const { t } = useTranslation();

  const { data, loading, error, refetch } = useQuery<
    { getArchivedCafeteriaItems: ArchivedItem[] },
    { contractCode: string }
  >(MY_CAFETERIA_GET_ARCHIVED_STATEMENT_ITEMS as DocumentNode, {
    skip: !contractCode,
    fetchPolicy: "cache-and-network",
    pollInterval: useOcPolling(1000 * 60),
    variables: { contractCode: contractCode! },
  });

  const { error: subscriptionError } = useSubscription(
    MY_CAFETERIA_SUBSCRIPTION as DocumentNode,
    {
      skip: !contractCode,
      onData: () => {
        refetch();
      },
    },
  );

  const parsedData: ParsedArchivedItem[] =
    data?.getArchivedCafeteriaItems?.map((item, index) => {
      // Deep clone to avoid mutating the original
      const newItem = _.cloneDeep(item) as any as ParsedArchivedItem;

      const isPlanLabel = item.plan
        ? t("MY_CAFETERIA_PLAN")
        : t("MY_CAFETERIA_FACT");

      newItem.index = index;
      newItem.periodStartDate = item.periodStartDate;
      newItem.originalStartDate = item.startDate;
      // Format month-only strings
      newItem.startDate = moment(parseJdDate(item.startDate)).format("MM");
      newItem.endDate = moment(parseJdDate(item.endDate)).format("MM");
      newItem.originalEndDate = item.endDate;
      newItem.months = `${newItem.startDate} - ${newItem.endDate}`;

      const numberOfMonths =
        Number(newItem.endDate) - Number(newItem.startDate) + 1;
      newItem.numberOfMonths = numberOfMonths;

      newItem.valueOfBenefit = item.cost;
      // Recalculate cost after benefit
      newItem.cost = item.cost - item.amount * numberOfMonths;
      newItem.benefit = item.menuItem?.name;
      newItem.origin = `${item.origin} - ${isPlanLabel}`;
      newItem.amount = item.amount * numberOfMonths;

      return newItem;
    }) ?? [];

  return {
    data: parsedData,
    loading,
    error: error || subscriptionError,
  };
};

export default useArchivedStatementItems;
